import React, { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../api'
import type { PlaceCategory } from '../api/placeCategory'
import type { RegisterPlaceRequest } from '../api/map'
import { toastUtil } from '../../utils/toast'

interface AdminKakaoPlaceFormProps {
  /** 상호명 입력칸의 초기값 (제안에서 넘어온 값) */
  initialName?: string
  /** 등록 실행. 제안 등록/직접 등록 어느 쪽인지는 부모가 결정한다. */
  onSubmit: (data: RegisterPlaceRequest) => Promise<void>
  /** 등록 버튼 문구 */
  submitLabel?: string
}

/**
 * 카카오맵 링크를 붙여넣어 장소를 등록하는 폼.
 *
 * 링크에서 읽어온 값을 그대로 저장하지 않고 입력칸에 채워만 준다.
 * 카카오 데이터가 늘 맞지는 않기 때문이다 — 예를 들어 광주 '치읓의자리'의
 * 주소를 카카오는 '전남광주통합특별시 북구…'로 준다. 관리자가 확인하고
 * 고칠 수 있어야 한다.
 *
 * 링크 해석에 실패해도 상호명·주소를 직접 입력해 등록할 수 있다.
 * 다만 좌표는 주소에서 얻으므로 주소가 정확해야 한다.
 */
const AdminKakaoPlaceForm: React.FC<AdminKakaoPlaceFormProps> = ({
  initialName = '',
  onSubmit,
  submitLabel = '장소로 등록',
}) => {
  const [categories, setCategories] = useState<PlaceCategory[]>([])
  const [url, setUrl] = useState('')
  const [isResolving, setIsResolving] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)
  /** 링크 해석이 카카오 차단으로 실패했는지 — 안내 문구를 바꾼다 */
  const [linkBlocked, setLinkBlocked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 링크에서 읽어온 뒤 관리자가 고칠 수 있는 값들
  const [name, setName] = useState(initialName)
  const [address, setAddress] = useState('')
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [kakaoPlaceId, setKakaoPlaceId] = useState('')
  // 카카오 분류가 틀릴 때가 있어 관리자가 직접 적는다. 비우면 라벨 없이 등록된다.
  const [subCategory, setSubCategory] = useState('')
  const [categoryId, setCategoryId] = useState<number | null>(null)

  // 이미 등록된 장소일 때의 안내
  const [existingPlace, setExistingPlace] = useState<{
    id: number
    name: string
  } | null>(null)

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await adminApi.getPlaceCategories()
        setCategories(response.data)
      } catch {
        toastUtil.error('카테고리 목록을 불러오지 못했습니다')
      }
    }
    fetchCategories()
  }, [])

  const handleResolve = useCallback(async () => {
    if (!url.trim()) {
      toastUtil.error('카카오맵 링크를 입력해주세요')
      return
    }

    setIsResolving(true)
    setExistingPlace(null)

    try {
      const result = await adminApi.resolveKakaoPlaceLink(url.trim())

      // 읽어온 값만 채운다. 못 읽은 항목은 기존 입력값을 지우지 않는다.
      if (result.name) setName(result.name)
      if (result.address) setAddress(result.address)
      if (result.lat !== null) setLat(String(result.lat))
      if (result.lng !== null) setLng(String(result.lng))
      setKakaoPlaceId(result.kakao_place_id)

      if (result.existing_place) {
        setExistingPlace(result.existing_place)

        /*
         * 이미 있는 장소면 저장된 값으로 네 칸을 채운다.
         *
         * 등록할 때 서버는 이 카카오 장소를 찾아 그대로 쓰고, 보내온
         * 상호명·주소·좌표는 버린다. 그런데 화면은 그 네 칸이 다 차야
         * 버튼을 열어줘서, 카카오가 막힌 지금은 버려질 값을 손으로 적고
         * 좌표 찾기까지 눌러야 했다. 할 일이 없는 경우에 일을 시킨 셈이다.
         */
        const e = result.existing_place
        if (e.name) setName(e.name)
        if (e.address) setAddress(e.address)
        if (e.lat !== null && e.lat !== undefined) setLat(String(e.lat))
        if (e.lng !== null && e.lng !== undefined) setLng(String(e.lng))
      }

      if (result.warning) {
        /*
         * 카카오가 2026-09-22부터 장소 페이지를 막아(403 Restricted User Agent)
         * 상호명·주소가 비어 온다. 장소 ID는 링크에서 뽑으므로 그대로 남는다.
         * 여기서 빨간 토스트만 띄우면 막다른 길로 보여, 남은 절차를 알려준다.
         */
        // 이미 있는 장소면 값을 다 채워뒀으므로 직접 입력하라고 하지 않는다.
        if (result.existing_place) {
          setLinkBlocked(false)
          toastUtil.success('이미 등록된 장소입니다. 등록 버튼만 누르면 됩니다')
        } else {
          setLinkBlocked(true)
          toastUtil.info('상호명과 주소를 직접 입력해주세요')
        }
      } else if (result.lat === null) {
        toastUtil.error('주소를 좌표로 바꾸지 못했습니다. 주소를 확인해주세요')
      } else {
        setLinkBlocked(false)
        toastUtil.success('링크에서 정보를 불러왔습니다')
      }
    } catch (error: any) {
      toastUtil.error(
        error?.response?.data?.message || '링크를 해석하지 못했습니다'
      )
    } finally {
      setIsResolving(false)
    }
  }, [url])

  /**
   * 주소만 고친 뒤 좌표를 다시 얻고 싶을 때.
   *
   * 예전에는 좌표를 얻는 길이 링크 해석 안에만 있었다. 2026-09-22부터 카카오가
   * 장소 페이지를 403(Restricted User Agent)으로 막으면서 상호명·주소가 비어
   * 오게 됐는데, 그 둘은 손으로 채울 수 있어도 좌표는 방법이 없어 등록 버튼이
   * 영영 잠겼다. 주소만으로 좌표를 얻는 길을 따로 연다.
   */
  const handleGeocode = useCallback(async () => {
    const target = address.trim()
    if (!target) {
      toastUtil.error('주소를 먼저 입력해주세요')
      return
    }

    setIsGeocoding(true)
    try {
      const {
        lat: foundLat,
        lng: foundLng,
        address: cleaned,
      } = await adminApi.geocodeAddress(target)

      setLat(String(foundLat))
      setLng(String(foundLng))

      /*
       * 카카오맵에서 복사하면 '… 26 1층'처럼 층·호수가 따라온다. 주소 검색이
       * 도로명까지만 정리해서 돌려주므로 그 값으로 바꿔 넣는다. 저장되는 표기가
       * 앱에서 등록되는 장소와 같아진다.
       */
      if (cleaned && cleaned !== target) {
        setAddress(cleaned)
        toastUtil.success(`좌표를 찾고 주소를 정리했습니다 — ${cleaned}`)
      } else {
        toastUtil.success('좌표를 찾았습니다')
      }
    } catch (error: any) {
      toastUtil.error(
        error?.response?.data?.message || '주소를 좌표로 바꾸지 못했습니다'
      )
    } finally {
      setIsGeocoding(false)
    }
  }, [address])

  const isReadyToSubmit =
    name.trim() !== '' &&
    address.trim() !== '' &&
    lat.trim() !== '' &&
    lng.trim() !== '' &&
    categoryId !== null

  const handleSubmit = async () => {
    if (!isReadyToSubmit || categoryId === null) return

    const latitude = Number(lat)
    const longitude = Number(lng)

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      toastUtil.error('좌표가 올바르지 않습니다')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({
        name: name.trim(),
        address: address.trim(),
        lat: latitude,
        lng: longitude,
        place_category_id: categoryId,
        kakao_place_id: kakaoPlaceId.trim() || null,
        sub_category: subCategory.trim() || null,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass = `
    w-full
    py-10px
    px-12px
    border
    border-brandPrimary-20%
    rounded-8px
    text-style-body2
    text-textColorText2
    bg-transparent
    focus:outline-none
    focus:border-brandPrimary
    placeholder:text-textColorText3
  `

  const labelClass = 'text-style-labelS text-textColorText2 mb-4px block'

  return (
    <div className="flex flex-col gap-16px">
      {/* 1단계: 링크 붙여넣기 */}
      <div>
        <label className={labelClass}>카카오맵 링크</label>
        <div className="flex gap-8px">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleResolve()
              }
            }}
            placeholder="https://place.map.kakao.com/496997032"
            className={inputClass}
          />
          <button
            type="button"
            onClick={handleResolve}
            disabled={isResolving}
            className={`
              shrink-0
              py-10px
              px-16px
              rounded-8px
              text-style-button2
              transition-colors
              ${
                isResolving
                  ? 'bg-brandPrimary-20% text-textColorText3 cursor-not-allowed'
                  : 'bg-4B3621-80% text-white hover:bg-4B3621-80%-70%'
              }
            `}
          >
            {isResolving ? '불러오는 중' : '불러오기'}
          </button>
        </div>
        {linkBlocked ? (
          <p className="text-style-labelS text-textColorText2 mt-6px leading-relaxed">
            카카오가 링크에서 가게 정보를 읽는 것을 막고 있어 상호명·주소가
            비어 있습니다. 장소 ID는 링크에서 가져왔으니
            <b> 카카오맵 화면을 보고 상호명과 주소만 입력</b>한 뒤
            <b> 좌표 찾기</b>를 누르면 그대로 등록됩니다.
          </p>
        ) : (
          <p className="text-style-labelS text-textColorText3 mt-6px">
            카카오맵에서 가게를 찾아 공유 링크를 붙여넣으면 상호명·주소·좌표를
            채워줍니다. 값은 저장 전에 고칠 수 있습니다.
          </p>
        )}
      </div>

      {/* 이미 등록된 장소면 등록 전에 알려준다 */}
      {existingPlace && (
        <div className="p-12px rounded-8px bg-backgroundColorBg2 border border-brandPrimary-20%">
          <div className="text-style-body2 text-textColorText2">
            이미 등록된 장소입니다 — {existingPlace.name} (ID{' '}
            {existingPlace.id})
          </div>
          <div className="text-style-labelS text-textColorText3 mt-4px">
            더 입력할 것 없이 아래 등록 버튼만 누르면 됩니다. 새로 만들지 않고
            이 장소를 제안에 연결합니다.
          </div>
        </div>
      )}

      {/* 2단계: 확인·수정 */}
      <div className="grid grid-cols-2 gap-12px">
        <div className="col-span-2">
          <label className={labelClass}>상호명</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="상호명"
            className={inputClass}
          />
        </div>

        <div className="col-span-2">
          <label className={labelClass}>주소</label>
          <div className="flex gap-8px">
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleGeocode()
                }
              }}
              placeholder="도로명 주소"
              className={inputClass}
            />
            <button
              type="button"
              onClick={handleGeocode}
              disabled={isGeocoding || address.trim() === ''}
              className="shrink-0 px-16px rounded-8px bg-4B3621-80% text-white text-style-button2 disabled:opacity-40"
            >
              {isGeocoding ? '찾는 중' : '좌표 찾기'}
            </button>
          </div>
        </div>

        <div>
          <label className={labelClass}>위도</label>
          <input
            type="text"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="'좌표 찾기'로 채웁니다"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>경도</label>
          <input
            type="text"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="'좌표 찾기'로 채웁니다"
            className={inputClass}
          />
        </div>

        <div className="col-span-2">
          <label className={labelClass}>카테고리</label>
          <div className="flex gap-8px">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setCategoryId(category.id)}
                className={`
                  py-8px
                  px-16px
                  rounded-8px
                  text-style-button2
                  border
                  transition-colors
                  ${
                    categoryId === category.id
                      ? 'bg-4B3621-80% text-white border-transparent'
                      : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                  }
                `}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-2">
          <label className={labelClass}>세부 분류 (선택)</label>
          <input
            type="text"
            value={subCategory}
            onChange={(e) => setSubCategory(e.target.value)}
            placeholder="예: 카페, 한식, 베이커리 — 비워도 됩니다"
            className={inputClass}
          />
          <p className="text-style-labelS text-textColorText3 mt-4px">
            장소 이름 옆에 붙는 작은 라벨입니다.
          </p>
        </div>

        <div className="col-span-2">
          <label className={labelClass}>카카오 장소 ID (선택)</label>
          <input
            type="text"
            value={kakaoPlaceId}
            onChange={(e) => setKakaoPlaceId(e.target.value)}
            placeholder="링크를 불러오면 자동으로 채워집니다"
            className={inputClass}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isReadyToSubmit || isSubmitting}
        className={`
          w-full
          py-12px
          rounded-8px
          text-style-button2
          transition-colors
          ${
            !isReadyToSubmit || isSubmitting
              ? 'bg-brandPrimary-20% text-textColorText3 cursor-not-allowed'
              : 'bg-4B3621-80% text-white hover:bg-4B3621-80%-70%'
          }
        `}
      >
        {isSubmitting ? '등록 중...' : submitLabel}
      </button>
    </div>
  )
}

export default AdminKakaoPlaceForm
